import { useMutation, useQuery } from 'react-query';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/apiClient';

/**
 * Contact-us ALLOWED E-MAIL LIST — the departments a visitor can address on the
 * website's /academics/contact-us form, and which of them get a copy of the
 * message by e-mail.
 *
 * Every call goes through `services/apiClient`, which applies the Backend API
 * Contract in one place. See DPA-FE `docs/APICall/BackendAPIContract.md`.
 *
 * ROUTES — verified against the live API on 2026-09-22 with a full
 * create/read/update/delete round trip (the probe record was removed again):
 *
 *   GET    /api/ContactUs/Settings           list   -> data is a BARE ARRAY
 *   GET    /api/ContactUs/Settings/{email}   one    -> data is the entity
 *   POST   /api/ContactUs/Settings           create -> HTTP 201 + the entity
 *   PUT    /api/ContactUs/Settings/{email}   update -> HTTP 200 + the entity
 *   DELETE /api/ContactUs/Settings/{email}   delete -> HTTP 200
 *
 * THE ENTITY
 *
 *   { email, title, isEmailSentRequired, isActive, mobile }
 *
 * `email` IS THE PRIMARY KEY. There is no id. It is also the join key to a
 * stored message's `receiverEmail`, which is how a message is attributed to a
 * department — see the rename warning on `useUpdateContactSetting`.
 *
 * ⚠ `mobile` IS NOT ON THE API YET. Confirmed against the live Swagger schema
 * on 2026-09-22 — `ContactUsAllowedEmailDto` / `…CreateRequest` / `…sRequest`
 * still carry only the four fields above. The backend team has agreed to add
 * it: a **mandatory** string, **digits and a leading `+` only**. Sent below
 * regardless, because a probe POST with an unrecognised `mobile` field today
 * returns 201 and simply drops it — ASP.NET ignores unknown JSON properties
 * rather than rejecting the request — so shipping the client ahead of the
 * field is harmless. It starts persisting for real the day the backend
 * deploys, with no client change needed. Tracked in
 * `DPA-FE/docs/APICall/DPA-CONFORMANCE.md`.
 *
 * THINGS MEASURED ON THE LIVE API THAT THE SWAGGER DOES NOT SAY
 *
 * - **The list is NOT paginated and NOT filtered.** `GET /Settings` returns
 *   every row including `isActive: false` ones, sorted by `title`. Anything
 *   that should only show live recipients has to filter client-side.
 * - **`GET /Settings` is PUBLIC**; every write requires a bearer token. An
 *   unauthenticated write answers 401 with an EMPTY body — no envelope — so
 *   `apiClient`'s rethrow falls back to the axios message for that one case.
 * - **`title` is required on create AND update.** An empty string is rejected
 *   with `errors.Title`. So a partial update is not possible: PUT must carry
 *   the whole entity, which is why `useUpdateContactSetting` takes every field
 *   rather than a patch.
 * - **Validation errors are a proper envelope** — HTTP 400, `success: false`,
 *   `error` carrying the sentence for the user and `errors` keyed by the
 *   PascalCase field name (`Email`, `Title`). `apiClient` already surfaces
 *   both; callers read `error.errors?.Email` for field-level placement.
 * - **DELETE is a hard delete and is NOT idempotent** — a second DELETE of the
 *   same address answers 404 `Record not found.` (Contract §5.6 asks for
 *   idempotency; this is a deviation, recorded in DPA-CONFORMANCE.md.)
 * - The e-mail in the path is matched case-insensitively, and percent-encoding
 *   it works, so `encodeURIComponent` below is safe as well as correct.
 */

const BASE = 'ContactUs/Settings';

/** The address is a path segment, so it has to be escaped before it is one. */
const pathFor = (email) => `${BASE}/${encodeURIComponent(email ?? '')}`;

/**
 * Every configured address, active or not.
 *
 * `select` normalises to an array so a caller never has to test the shape: the
 * endpoint answers with a bare array today, and `{ items: [] }` would be the
 * contract-shaped alternative if it is ever paginated.
 */
export const useFetchContactSettings = () =>
  useQuery({
    queryKey: ['contactSettings'],
    // `apiGet`, not `apiGetList`: this is a whole-collection document with no
    // pagination. `apiGetList` would append `/1/0` to the path and 404.
    queryFn: () => apiGet(BASE),
    refetchOnWindowFocus: false,
    select: (data) =>
      (Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []),
  });

/** One address. Off by default — screens that need it fetch it deliberately. */
export const useFetchContactSettingByEmail = (email, { enabled = false } = {}) =>
  useQuery({
    queryKey: ['contactSetting', email],
    queryFn: () => apiGet(pathFor(email)),
    enabled: enabled && !!email,
    refetchOnWindowFocus: false,
  });

/** POST. Rejects a duplicate address with 400 `errors.Email`. */
export const useCreateContactSetting = () =>
  useMutation(({ email, title, isEmailSentRequired, isActive, mobile }) =>
    apiPost(BASE, { email, title, isEmailSentRequired, isActive, mobile })
  );

/**
 * PUT. `originalEmail` addresses the record; `email` in the body is the value
 * it will HAVE afterwards, so the two differ only when an address is renamed.
 *
 * RENAMING IS DESTRUCTIVE TO ATTRIBUTION. Messages already stored keep the OLD
 * `receiverEmail`, and nothing rewrites them, so every past message for that
 * department stops matching its recipient row — it still lists, but under the
 * bare address instead of the department name. The screen warns before saving
 * a rename; this is why.
 *
 * All four fields are sent every time: the backend validates `title` as
 * required on PUT, so there is no partial update to make.
 */
export const useUpdateContactSetting = () =>
  useMutation(
    ({ originalEmail, email, title, isEmailSentRequired, isActive, mobile }) =>
      apiPut(pathFor(originalEmail ?? email), {
        email,
        title,
        isEmailSentRequired,
        isActive,
        mobile,
      })
  );

/** DELETE. Hard delete — see the note above about it not being idempotent. */
export const useDeleteContactSetting = () =>
  useMutation((email) => apiDelete(BASE, encodeURIComponent(email ?? '')));
